// src/components/ModulesList.js
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../supabase';

const ModulesList = ({ setCurrentThread = () => {} }) => {
  const navigate = useNavigate();
  const [userId, setUserId] = useState(null);
  const [modules, setModules] = useState([]);
  const [completions, setCompletions] = useState([]); // module completions
  const [flowCompletions, setFlowCompletions] = useState([]); // dialog flow completions
  const [userContext, setUserContext] = useState({});
  const [moduleRules, setModuleRules] = useState([]);
  const [dialogAssociations, setDialogAssociations] = useState([]);
  const [dialogFlows, setDialogFlows] = useState([]);
  const [dialogFlowRules, setDialogFlowRules] = useState([]); // new state for flow rules
  const [activities, setActivities] = useState([]); // new state for module activities
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // New state for filtering by module_type
  const [filterType, setFilterType] = useState('all');

  // Fetch authenticated user
  useEffect(() => {
    const fetchUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUserId(session.user.id);
      }
    };
    fetchUser();
  }, []);

  // Fetch modules, completions, flow completions, user context, rules, associations, flows, flow rules, and activities in parallel
  useEffect(() => {
    if (!userId) return;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [
          { data: modData, error: modErr },
          { data: compData, error: compErr },
          { data: flowCompData, error: flowCompErr },
          { data: ctxData, error: ctxErr },
          { data: ruleData, error: ruleErr },
          { data: assocData, error: assocErr },
          { data: flowData, error: flowErr },
          { data: flowRuleData, error: flowRuleErr },
          { data: activitiesData, error: activitiesErr },
        ] = await Promise.all([
          supabase.from('modules').select('*').order('display_order', { ascending: true }),
          supabase.from('module_completions').select('*').eq('user_id', userId),
          supabase.from('dialog_flow_completions').select('*').eq('user_id', userId),
          supabase.from('user_context').select('*').eq('user_id', userId),
          supabase.from('module_rules').select('*'),
          supabase.from('module_dialog_flows').select('*'),
          supabase.from('dialog_flows').select('*'),
          supabase.from('dialog_flow_rules').select('*'),
          supabase.from('module_activities').select('*').order('order_index', { ascending: true }),
        ]);

        if (modErr) throw modErr;
        if (compErr) throw compErr;
        if (flowCompErr) throw flowCompErr;
        if (ctxErr) throw ctxErr;
        if (ruleErr) throw ruleErr;
        if (assocErr) throw assocErr;
        if (flowErr) throw flowErr;
        if (flowRuleErr) throw flowRuleErr;
        if (activitiesErr) throw activitiesErr;

        const contextObj = {};
        if (ctxData) {
          ctxData.forEach((item) => {
            let value = item.value;
            if (typeof value === 'string') {
              value = value.replace(/^"|"$/g, '');
            }
            contextObj[item.key] = value;
          });
        } else {
          console.warn('No user context found yet.');
        }

        setModules(modData || []);
        setCompletions(compData || []);
        setFlowCompletions(flowCompData || []);
        setUserContext(contextObj);
        setModuleRules(ruleData || []);
        setDialogAssociations(assocData || []);
        setDialogFlows(flowData || []);
        setDialogFlowRules(flowRuleData || []); // save new flow rules
        setActivities(activitiesData || []);
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load modules or dialog flows. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId]);

  // Rule evaluation functions for both module and dialog flow rules.
  const evaluateRule = (rule, context) => {
    const userValue = (context[rule.condition_key] || '').toLowerCase().trim();
    const expected = rule.expected_value.toLowerCase().trim();
    switch (rule.comparison_operator) {
      case 'equals':
        return userValue === expected;
      case 'contains':
        return userValue.includes(expected);
      default:
        return false;
    }
  };

  const evaluateModuleRules = (mod) => {
    const rulesForModule = moduleRules.filter(
      (rule) =>
        rule.module_id === mod.id &&
        (rule.phase === null || rule.phase === mod.phase)
    );
    if (rulesForModule.length === 0) return true;
    return rulesForModule.every((rule) => evaluateRule(rule, userContext));
  };

  // New: evaluate dialog flow rules for a given flow.
  const shouldDisplayFlow = (flow) => {
    const rulesForFlow = dialogFlowRules.filter(
      (rule) => rule.dialog_flow_id === flow.id
    );
    // If no rules exist, show the flow.
    if (rulesForFlow.length === 0) return true;
    // Otherwise, every rule must pass.
    return rulesForFlow.every((rule) => evaluateRule(rule, userContext));
  };

  const filteredModules = useMemo(() => {
    return modules.filter((mod) => evaluateModuleRules(mod));
  }, [modules, moduleRules, userContext]);

  // Apply additional filter for module_type
  const displayedModules = useMemo(() => {
    return filteredModules.filter((mod) => 
      filterType === 'all' ? true : mod.module_type === filterType
    );
  }, [filteredModules, filterType]);

  // Compute unique module types from the modules, defaulting to 'uncategorized' if none provided.
  const moduleTypes = useMemo(() => {
    const types = new Set(
      modules.map((mod) => mod.module_type ? mod.module_type : 'uncategorized')
    );
    return ['all', ...Array.from(types)];
  }, [modules]);
  

  // Return dialog flows associated with a module and filter by the new flow rules.
  const getDialogFlowsForModule = (moduleId) => {
    const associations = dialogAssociations.filter(
      (assoc) => assoc.module_id === moduleId
    );
    return associations
      .map((assoc) => dialogFlows.find((flow) => flow.id === assoc.dialog_flow_id))
      .filter(Boolean)
      .filter((flow) => shouldDisplayFlow(flow));
  };

  // Return module activities for a given module.
  const getActivitiesForModule = (moduleId) => {
    return activities.filter((activity) => activity.module_id === moduleId);
  };

  // Check if a flow is completed.
  const isFlowCompleted = (flowId) => {
    return flowCompletions.some((fc) => fc.dialog_flow_id === flowId);
  };

  // Create a new thread for a dialog flow.
  const createThreadForFlow = async (dialogFlowId) => {
    const { data: flowData, error: flowError } = await supabase
      .from('dialog_flows')
      .select('name')
      .eq('id', dialogFlowId)
      .single();
    if (flowError) {
      console.error('Error fetching dialog flow:', flowError);
    }
    const threadName = flowData?.name ? flowData.name : `${dialogFlowId}`;
    const { data, error } = await supabase
      .from('threads')
      .insert({
        user_id: userId,
        name: threadName,
        flow_id: dialogFlowId,
        created_at: new Date().toISOString(),
      })
      .select('*')
      .single();
    if (error) {
      console.error('Error creating thread:', error);
      return null;
    }
    return data;
  };

  // Start a dialog flow by creating a thread and navigating.
  const startDialogFlow = async (dialogFlowId) => {
    console.log('Starting dialog flow:', dialogFlowId);
    const newThread = await createThreadForFlow(dialogFlowId);
    if (newThread) {
      setCurrentThread(newThread);
      navigate(`/assistant?threadId=${newThread.id}&flowId=${dialogFlowId}`);
    } else {
      console.error('Failed to create thread for flow:', dialogFlowId);
    }
  };

  // Start an activity by navigating to its page.
  const startActivity = (pageUrl) => {
    navigate(`/activity/${pageUrl}`);
  };

  return (
    <div className="p-6 bg-gray-900 text-white rounded-md shadow-md max-w-5xl mx-auto">
      {/* Submenu for filtering modules by module_type */}
      <div className="mb-4 flex flex-wrap gap-2">
        {moduleTypes.map((type) => (
          <button
            key={type}
            onClick={() => setFilterType(type)}
            className={`px-3 py-1 rounded-full text-sm ${
              filterType === type ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {type.charAt(0).toUpperCase() + type.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-10">
          <svg className="animate-spin h-10 w-10 text-blue-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
          </svg>
          <p className="text-gray-400">Loading modules and flows...</p>
        </div>
      ) : error ? (
        <div className="bg-red-600 p-4 rounded text-center">
          <p className="text-white">{error}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {displayedModules.length > 0 ? (
            displayedModules.map((mod) => {
              const flows = getDialogFlowsForModule(mod.id);
              const totalFlows = flows.length;
              const completedFlows = flows.filter((flow) => isFlowCompleted(flow.id)).length;
              // Get module activities for the module
              const moduleActivities = getActivitiesForModule(mod.id);
              return (
                <div key={mod.id} className="bg-gray-800 p-6 rounded-lg shadow transition-shadow duration-300">
                  <h3 className="text-2xl font-bold mb-4">{mod.name}</h3>
                  
                  {/* Activities Section */}
                  {moduleActivities.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-lg font-semibold mb-2">Settings</h4>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {moduleActivities.map((activity) => (
                          <li key={activity.id}>
                            <button
                              onClick={() => startActivity(activity.page_url)}
                              className="w-full text-center px-3 py-2 bg-blue-200 text-gray-900 hover:bg-blue-300 rounded"
                            >
                              {activity.activity_name}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Dialog Flows Section */}
                  {flows.length > 0 ? (
                    <div>
                      <h4 className="text-lg font-semibold mb-2">Activities</h4>
                      <ul className="space-y-2">
                        {flows.map((flow) => {
                          const completed = isFlowCompleted(flow.id);
                          return (
                            <li key={flow.id} className="flex items-center">
                              {completed ? (
                                <span className="text-green-400 mr-2">✔</span>
                              ) : (
                                <span className="text-gray-400 mr-2">○</span>
                              )}
                              <button
                                onClick={() => startDialogFlow(flow.id)}
                                disabled={completed}
                                className={`text-left flex-1 transition-colors duration-200 ${
                                  completed ? 'text-gray-400 cursor-not-allowed' : 'text-blue-200 hover:text-blue-100'
                                }`}
                              >
                                {flow.name}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">No associated dialog flows.</p>
                  )}
                </div>
              );
            })
          ) : (
            <p className="text-gray-400 col-span-full text-center">
              No modules available for your current context.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default ModulesList;
